import { useCallback, useContext, useMemo, useState } from "react";
import { Event, EventType, NotificationType } from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import ButtonWithDropdown from "../Buttons/ButtonWithDropdown";
import { DropdownMenuItem } from "../DropdownMenu";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import ActiveInactiveLabel from "./ActiveInactiveLabel";
import { EventContentMobile } from "../LandingPage/RenderedEvents";
import {
  GetDateWithTime,
  isEventRelatedType,
  LessThanDate,
} from "@markit/common.utils";
import { deleteEventData, eventActions } from "../../redux/slices/eventSlice";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import { MixpanelContext } from "../../context/AnalyticsService";
import { getAccountState } from "../../redux/slices/accountSlice";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { updateEventManualSoldOut } from "../../utils/dashboardUtils";
import {
  NavigationId,
  NewProductNavigationId,
} from "../../navigation/AppParamList";
import { FormProfileItemImage } from "./FormItem/FormProfileItemImage";
import CloneProductModal from "../EventDashboard/DashboardModals/CloneProductModal";
import EventItemPreviewType from "./EventItem/EventItemPreviewType";

export const FORM_ITEM_FLEX_SECTIONS = [3, 2, 6];

type ProductItemPreviewProps = {
  item: Event;
  isProfileForm?: boolean;
};

// The single row renderer for every item listed on the Products panel
export const ProductItemPreview = ({
  item,
  isProfileForm,
}: ProductItemPreviewProps) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const { uid } = useSelector(getAccountState).account.accountData;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const styles = {
    itemBorder: { border: `1px solid ${theme.SubText.color}` },
  };

  // "Form" for forms, "Event" for ticketed/webinar events, "Product" for everything else
  const itemLabel = useMemo(() => {
    if (item.eventType === EventType.GENERIC_LINK) {
      return "Form";
    }
    return isEventRelatedType(item.eventType) ? "Event" : "Product";
  }, [item.eventType]);

  // Mirrors sortProductsAndEventsList's own active/inactive rule: event-related types
  // go by end date, everything else goes by the isManualSoldOut toggle
  const isActive = useMemo(
    () =>
      isEventRelatedType(item.eventType)
        ? LessThanDate(new Date().toISOString(), item.end)
        : !item.isManualSoldOut,
    [item.eventType, item.end, item.isManualSoldOut]
  );

  const navigateDashboard = useCallback(() => {
    if (isProfileForm) {
      navigate(NavigationId.HOME_SUBSCRIBE_FORM);
    } else if (item.isDraft) {
      navigate(
        item.eventType === EventType.WEBINAR
          ? NewProductNavigationId.WEBINAR
          : NewProductNavigationId.EVENT,
        { state: { eventId: item.id } }
      );
    } else {
      navigate(`/e/${item.id}/dashboard`, {
        state: { eventId: item.id },
      });
    }
  }, [isProfileForm, item.id, item.isDraft, item.eventType, navigate]);

  const switchItemStatus = useCallback(async () => {
    await updateEventManualSoldOut(item);
    dispatch(
      eventActions.updateEvent({
        ...item,
        isManualSoldOut: !item.isManualSoldOut,
      })
    );
    showNotificationBanner(
      dispatch,
      item.isManualSoldOut
        ? `${itemLabel} Activated`
        : `${itemLabel} Deactivated`,
      item.isManualSoldOut
        ? NotificationType.AFFIRMATIVE
        : NotificationType.NEGATIVE,
      item.isManualSoldOut ? "ion:checkmark-circle" : "ion:close-circle"
    );
  }, [dispatch, item, itemLabel]);

  const deleteItemOnPress = useCallback(() => {
    // Be mindful of forms with over 100 submissions (due to batch size limit)
    dispatch(deleteEventData(item, uid, mixpanel));
    showNotificationBanner(
      dispatch,
      `${itemLabel} Deleted`,
      NotificationType.NEGATIVE,
      "ion:trash"
    );
  }, [dispatch, item, mixpanel, uid, itemLabel]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "Clone",
        icon: "ion:copy",
        key: "clone",
        onPress: () => {
          setShowCloneModal(true);
          setShowDropdown(false);
        },
      },
    ];

    if (!isProfileForm) {
      items.push(
        {
          title: item.isManualSoldOut ? "Activate" : "Deactivate",
          icon: item.isManualSoldOut
            ? "ion:checkmark-circle"
            : "ion:close-circle",
          key: "deactivate",
          onPress: switchItemStatus,
        },
        {
          title: "Delete",
          icon: "ion:trash",
          key: "delete",
          onPress: () => {
            setShowDeleteModal(true);
            setShowDropdown(false);
          },
          isDestructive: true,
        }
      );
    }

    return items;
  }, [item.isManualSoldOut, switchItemStatus, isProfileForm]);

  return (
    <>
      <div className="AlignedRow" style={{ height: 64 }}>
        <div
          className="AlignedRowSelect"
          onClick={navigateDashboard}
          style={{ flex: FORM_ITEM_FLEX_SECTIONS[0], gap: 20 }}
        >
          {isProfileForm ? (
            <div className="AlignedRow" style={{ gap: 14 }}>
              <FormProfileItemImage item={item} size={70} />
              <div className="ColumnNormal" style={{ gap: 4, width: 240 }}>
                <span className="bodyMedium OneLineText">{item.title}</span>
                <span className="smallBodySubtext">
                  Updated: {GetDateWithTime(item.createdAt, true)}
                </span>
                <EventItemPreviewType
                  eventItem={item}
                  altFontSize={12}
                  altBackgroundColor={Colors.WHITE1}
                />
              </div>
            </div>
          ) : (
            <EventContentMobile event={item} myEvent altSize={70} />
          )}
        </div>
        {!item.isDraft ? (
          <div style={{ flex: FORM_ITEM_FLEX_SECTIONS[1] }}>
            <ActiveInactiveLabel
              isActive={isActive}
              containerStyles={{ marginLeft: 14 }}
            />
          </div>
        ) : (
          <div style={{ flex: FORM_ITEM_FLEX_SECTIONS[1] }} />
        )}
        <div
          className="AlignedRowSpaced"
          style={{
            flex: FORM_ITEM_FLEX_SECTIONS[2],
            justifyContent: "flex-end",
            gap: 24,
          }}
        >
          <ButtonWithDropdown
            buttonLabel="View"
            textColor={Colors.BLACK}
            isOpenDropdown={showDropdown}
            setIsOpenDropdown={setShowDropdown}
            onPress={navigateDashboard}
            dropdownItems={dropdownItems}
            containerStyles={styles.itemBorder}
          />
        </div>
      </div>
      {showDeleteModal ? (
        <ConfirmDeleteModal
          heading={`Delete ${itemLabel}?`}
          subtext={`Are you sure you want to delete this ${itemLabel.toLowerCase()}? This cannot be undone.`}
          deleteButtonText={`Delete ${itemLabel}`}
          icon={
            <Icon
              icon={"ion:trash"}
              height={35}
              style={{ color: Colors.RED3 }}
            />
          }
          hideModal={!showDeleteModal}
          setIsVisible={setShowDeleteModal}
          deleteOnPress={deleteItemOnPress}
        />
      ) : null}
      {showCloneModal ? (
        <CloneProductModal
          event={item}
          isModalVisible={showCloneModal}
          setIsModalVisible={setShowCloneModal}
        />
      ) : null}
    </>
  );
};
